import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import {
  useListTree,
  useWorkspacePages,
  useTableNodes,
  useEntityTypes,
} from "../../../../lib/hooks/use-kuvio";
import { useWorkspaceMembers } from "../../../hooks/use-workspace-members";
import { useAuth } from "../../../../lib/hooks/use-auth";
import { mcpClient } from "../../../../lib/mcp-client";
import { queryKeys, toolNames, displayFieldFor } from "../../../../lib/query-keys";
import { useRowDetailPanel } from "../../entity/RowDetailContext";
import type { LibraryNode, ListEntitiesResult } from "../../../../lib/types";
import type { Command, CommandContext } from "../types";

/**
 * Navigation commands (S3.09) — every named thing in the workspace becomes a
 * "Go to …" command: agents, folders, pages, tables, members. Built lazily from
 * the TanStack Query caches the rail/cockpit already populate (zero new
 * network calls). Memories are excluded for now — no client hook fetches them
 * and a deep memory scan risks the same DO result-limit overflow useListTree
 * documents.
 */

const TYPE_ICON: Record<string, string> = {
  agent: "user-circle",
  folder: "folder",
  page: "file-text",
  table: "table",
};

const TYPE_LABEL: Record<string, string> = {
  agent: "Agent",
  folder: "Folder",
  page: "Page",
  table: "Table",
};

export function useNavigationCommands(ctx: CommandContext): Command[] {
  const { currentWorkspaceId } = useAuth();
  const tree = useListTree(currentWorkspaceId ?? undefined); // agents + folders
  const pages = useWorkspacePages();
  const tables = useTableNodes();
  const members = useWorkspaceMembers();
  const entityTypes = useEntityTypes();
  const { openPanel } = useRowDetailPanel();

  // Sprint 12 — entity rows become navigation results too. Each row of every
  // synthesised entity type appears as "<display value> — <Type>" and opens the
  // row-detail panel (an overlay, not a route). The index mounts lazily on first
  // Cmd-K open, so these per-type lists aren't fetched on every page load.
  const rowTypes = useMemo(
    () => (entityTypes.data ?? []).filter((t) => !t.hide_from_synthesis),
    [entityTypes.data],
  );
  const rowQueries = useQueries({
    queries: rowTypes.map((t) => ({
      queryKey: [...queryKeys.entities(t.slug), "cmdk"] as const,
      queryFn: () => mcpClient.listEntities(toolNames(t).list),
      enabled: !!entityTypes.data,
      staleTime: 60_000,
    })),
  });

  const { navigate, rootNodeId } = ctx;

  return useMemo(() => {
    const treeNodes = tree.data ?? [];
    const pageNodes = pages.data ?? [];
    const tableNodes = tables.data ?? [];

    // Resolve breadcrumbs from the parent_id chain across everything we know.
    const nodeMap = new Map<string, LibraryNode>();
    for (const n of [...treeNodes, ...pageNodes, ...tableNodes]) nodeMap.set(n.id, n);

    function breadcrumb(node: LibraryNode): string {
      const parts: string[] = [];
      let pid = node.parent_id;
      let guard = 0;
      while (pid && guard++ < 16) {
        const parent = nodeMap.get(pid);
        if (!parent) break;
        parts.push(parent.name);
        pid = parent.parent_id;
      }
      return parts.reverse().join(" / ");
    }

    const cmds: Command[] = [];

    function pushNode(node: LibraryNode, to: string) {
      const crumb = breadcrumb(node);
      cmds.push({
        id: `nav:${node.type}:${node.id}`,
        label: node.name,
        icon: TYPE_ICON[node.type] ?? "circle",
        kind: "nav",
        subtitle: crumb || TYPE_LABEL[node.type] || "Item",
        keywords: [node.type, ...(crumb ? crumb.split(" / ") : [])],
        execute: () => navigate(to),
      });
    }

    for (const n of treeNodes) {
      if (n.type === "agent" || n.type === "folder") pushNode(n, `/agents/${n.id}`);
    }
    for (const n of pageNodes) pushNode(n, `/library/${n.id}`);
    for (const n of tableNodes) {
      const parent = n.parent_id ?? rootNodeId;
      if (!parent) continue;
      pushNode(n, `/agents/${parent}/tables/${n.id}`);
    }

    for (const m of members.data?.members ?? []) {
      const label = m.name || m.email;
      cmds.push({
        id: `nav:member:${m.id}`,
        label,
        icon: "user",
        kind: "nav",
        subtitle: m.name && m.email ? m.email : "Member",
        keywords: ["member", "people", m.email].filter(Boolean),
        execute: () => navigate("/settings/members"),
      });
    }

    const ROW_CAP = 50; // top N per type — recency boost ranks recently-opened higher
    rowTypes.forEach((t, i) => {
      const data = rowQueries[i]?.data as ListEntitiesResult | undefined;
      const df = displayFieldFor(t);
      for (const e of (data?.entities ?? []).slice(0, ROW_CAP)) {
        const dv = e[df];
        const label = typeof dv === "string" && dv.length > 0 ? dv : e.id;
        cmds.push({
          id: `row:${t.slug}:${e.id}`,
          label,
          icon: "list-details",
          kind: "nav",
          subtitle: t.name,
          keywords: [t.name, t.slug, e.id],
          execute: () => openPanel(t.slug, e.id),
        });
      }
    });

    return cmds;
  }, [tree.data, pages.data, tables.data, members.data, rowTypes, rowQueries, navigate, rootNodeId, openPanel]);
}
